// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  colors,
  copy,
  BackArrowButton,
  FullSpread,
  WhitespaceColumn,
  ContentColumn,
  Spinner,
} from '@peachjar/components';
import { Query } from 'react-apollo';
import { css } from 'react-emotion';
import config from '../../../../../../config';

import TempRightActionHelper from '../../../components/TempRightActionHelper';
import { storeDiscountCode } from '../../../onboarding.duck';
import PricingBox from '../components/PricingBox';
import ReferralBox from '../components/ReferralBox';
import { MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL } from '../../../constants';
import ONBOARDING_PRICE_QUERY from '../gql/schoolOrDistrictOnboardingPrice.query';
import DISCOUNT_AVAILABILITY_QUERY from '../gql/groupDiscountCodeForSchoolOnboarding.query';
import WhiteSpaceSvgWrapper from '../../../../../components/WhiteSpaceSvgWrapper';
import Trees from '../components/Trees';

const { dragon, jungle } = colors;

const styles = {
  header: css({
    fontSize: '34px',
    fontWeight: 600,
    color: '#2d344b',
    maxWidth: '512px',
    height: '44px',
  }),
};

const isDiscountError = errorMessage =>
  !!errorMessage.toLowerCase().indexOf('discount') ||
  !!errorMessage.toLowerCase().indexOf('code');

type State = {
  appliedDiscountCodeInput: string,
};

type Props = {
  nextPage: () => void,
  onStoreDiscountCode: discountCode => void,
  formValues: {
    numberOfSchools: number,
    discountCode: string,
  },
};

class PricingContainer extends Component<Props, State> {
  state = { appliedDiscountCodeInput: '' };

  handleTempDiscountCodeInput = appliedDiscountCodeInput => {
    this.setState({
      appliedDiscountCodeInput,
    });
  };

  render() {
    // the discount code input that is in state when the user hits 'apply'
    const { appliedDiscountCodeInput } = this.state;
    const discountCode = appliedDiscountCodeInput;

    const numberOfSchools = this.props.formValues.numberOfSchools;
    const { nextPage, onStoreDiscountCode } = this.props;
    const discountQueryVariables = { code: appliedDiscountCodeInput };

    return (
      <Query
        query={DISCOUNT_AVAILABILITY_QUERY}
        variables={discountQueryVariables}
        skip={appliedDiscountCodeInput === ''}
      >
        {({
          loading: discountLoading,
          error: discountError,
          networkStatus: discountNetworkStatus,
          data = {},
        }) => {
          const discountCodeFound =
            !!data &&
            !!data.groupDiscountCodeForSchoolOnboarding &&
            !!data.groupDiscountCodeForSchoolOnboarding.id;

          // this looks gross, but it ensures that a valid discountCode will
          // be put into the redux state and entered into the registrationForm
          if (
            discountCodeFound &&
            appliedDiscountCodeInput !== this.props.formValues.discountCode
          ) {
            this.props.onStoreDiscountCode(appliedDiscountCodeInput);
          }

          const priceQueryVariables = discountCodeFound
            ? { numberOfSchools, discountCode }
            : { numberOfSchools };

          return (
            <Query
              query={ONBOARDING_PRICE_QUERY}
              variables={priceQueryVariables}
              skip={numberOfSchools < 1 || numberOfSchools > 5}
            >
              {({ loading, priceError, data = {} }) => {
                const schoolOrDistrictOnboardingPrice =
                  data.schoolOrDistrictOnboardingPrice || {};
                const {
                  totalAmount,
                  discountRate,
                  startUpFeePerSchool,
                } = schoolOrDistrictOnboardingPrice;

                if (
                  priceError &&
                  priceError.graphQLErrors[0] &&
                  !isDiscountError(priceError.graphQLErrors[0].message)
                ) {
                  return <div>Error: {priceError}</div>;
                }

                return (
                  <FullSpread>
                    <ContentColumn
                      className={css({ paddingBottom: '2rem' })}
                      rightAction={<TempRightActionHelper />}
                      leftAction={
                        <BackArrowButton
                          url={config.REACT_APP_ONBOARDING_MARKETING_PAGE}
                        />
                      }
                    >
                      <h1 className={styles.header}>School District Pricing</h1>
                      <p style={{ maxWidth: '512px', textAlign: 'center' }}>
                        Pricing is based on how many schools you have. Enter the
                        total number of schools in your district to see your
                        one-time fee.
                      </p>
                      <PricingBox
                        MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL={
                          MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL
                        }
                        numberOfSchools={numberOfSchools}
                        pricePerSchool={startUpFeePerSchool}
                        totalAmount={totalAmount}
                        discountRate={discountRate}
                        onGetStarted={nextPage}
                      />

                      {numberOfSchools >
                      MAX_NUMBER_OF_SCHOOLS_ALLOWED_WITHOUT_PROPOSAL ? null : (
                        <ReferralBox
                          handleTempDiscountCodeInput={
                            this.handleTempDiscountCodeInput
                          }
                          numberOfSchools={numberOfSchools}
                          discountLoading={discountLoading}
                          discountNetworkStatus={discountNetworkStatus}
                        />
                      )}

                      {discountLoading &&
                        appliedDiscountCodeInput && <Spinner />}

                      {!discountLoading &&
                      appliedDiscountCodeInput &&
                      !discountCodeFound ? (
                        <div
                          className={css({ paddingTop: '2rem', color: dragon })}
                        >
                          Invalid discount code.
                        </div>
                      ) : null}

                      {appliedDiscountCodeInput && discountCodeFound ? (
                        <div
                          className={css({ paddingTop: '2rem', color: jungle })}
                        >
                          Your discount code was applied.
                        </div>
                      ) : null}
                    </ContentColumn>
                    <WhitespaceColumn>
                      <WhiteSpaceSvgWrapper
                        svg={<Trees numberOfSchools={numberOfSchools} />}
                        quote={copy.quotes.roberts_goGreen}
                      />
                    </WhitespaceColumn>
                  </FullSpread>
                );
              }}
            </Query>
          );
        }}
      </Query>
    );
  }
}

const mapStateToProps = state => {
  const form = state.form.onboarding;
  const values = form && form.values;
  const onboarding = state.onboarding;
  const discountCode = onboarding.discountCode || '';

  return {
    formValues: { ...values, discountCode },
  };
};

const mapDispatchToProps = dispatch => ({
  onStoreDiscountCode: discountCode => {
    dispatch(storeDiscountCode(discountCode));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PricingContainer);



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/Pricing/containers/PricingContainer.jsx